import { apiError } from "../../../../../utils/formErrors";
import React, { useState, useEffect } from "react";
import { Box, Stack, Dialog, TextField, IconButton, CircularProgress, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { createReviewCycle, updateReviewCycle, getReviewCycles } from "../../../../../services/performance";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../theme";

const Label = ({ children }) => <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>{children}</Box>;

/*
 * What a new cycle starts with.
 *
 * The design book specified exactly these three. They are a default rather than
 * a fixture — an org that wants different questions, or five of them, edits the
 * list — but starting from a blank form would leave most cycles with no written
 * half at all, which is the state this feature exists to fix.
 */
const DEFAULT_QUESTIONS = [
  "What went well against your goals?",
  "What did not, and what got in the way?",
  "What do you want to take on next cycle?",
];

const CycleFormDialog = ({ open, onClose, onDone, cycle }) => {
  const notify = useNotify();
  const [name, setName] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [scaleMax, setScaleMax] = useState(5);
  const [competencies, setCompetencies] = useState([{ name: "", weight: 1 }]);
  const [busy, setBusy] = useState(false);
  // The competency vocabulary, taken from the cycles that already exist.
  // Retyping a name each cycle is how "Ownership" becomes "Takes ownership" —
  // distinct strings that quietly stop an employee's ratings being comparable
  // across cycles, which is most of what the record is for. Suggesting the
  // established wording makes the consistent choice the easy one, and free text
  // still works for a competency that genuinely applies to one cycle only.
  const [known, setKnown] = useState([]); // [{ name, weight }]

  useEffect(() => {
    if (!open) return;
    getReviewCycles()
      .then((res) => {
        const seen = new Map();
        (res?.data || []).forEach((c) =>
          (c.competencies || []).forEach((comp) => {
            const label = (comp.name || "").trim();
            // newest cycle first, so the most recent weight is the one offered
            if (label && !seen.has(label)) seen.set(label, { name: label, weight: comp.weight ?? 1 });
          })
        );
        setKnown([...seen.values()]);
      })
      .catch(() => {}); // suggestions are a convenience; typing still works
  }, [open]);

  const asDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

  useEffect(() => {
    if (!open) return;
    // editing an existing draft, or a blank form for a new one
    setName(cycle?.name || "");
    setPeriodStart(asDate(cycle?.periodStart));
    setPeriodEnd(asDate(cycle?.periodEnd));
    setScaleMax(cycle?.ratingScale?.max || 5);
    setCompetencies(
      cycle?.competencies?.length
        ? cycle.competencies.map((c) => ({ name: c.name, weight: c.weight ?? 1 }))
        : [{ name: "", weight: 1 }]
    );
  }, [open, cycle]);

  const setComp = (i, f, v) => setCompetencies((p) => p.map((row, idx) => (idx === i ? { ...row, [f]: v } : row)));

  const submit = async () => {
    const comps = competencies.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), weight: Number(c.weight) || 1 }));
    if (!name.trim()) return notify.error("Name is required");
    if (comps.length === 0) return notify.error("Add at least one competency");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), periodStart: periodStart || undefined, periodEnd: periodEnd || undefined,
        ratingScale: { max: Number(scaleMax) || 5 }, competencies: comps,
        // A brand-new cycle starts from the standard set so it is never created
        // with no written half; editing them is the questions screen's job.
        ...(cycle ? {} : { questions: DEFAULT_QUESTIONS.map((prompt) => ({ prompt, askedOf: "both", required: true })) }),
      };
      const res = cycle ? await updateReviewCycle(cycle._id, payload) : await createReviewCycle(payload);
      if (res?.success === false) throw apiError(res);
      notify.success(cycle ? "Review cycle updated" : "Review cycle created");
      onDone?.(); onClose();
    } catch (e) { notify.error(e?.message || "Could not save cycle"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "12px", width: "min(34rem,94vw)" } }}>
      <Box sx={{ p: "1.5rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{cycle ? "Edit review cycle" : "New review cycle"}</Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <Label>Cycle name</Label>
        <TextField fullWidth size="small" value={name} placeholder="e.g. H1 2026 Appraisal" onChange={(e) => setName(e.target.value)} sx={{ mb: "1rem" }} />

        <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
          <Box sx={{ flex: 1 }}><Label>Period start</Label><TextField fullWidth size="small" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} InputLabelProps={{ shrink: true }} /></Box>
          <Box sx={{ flex: 1 }}><Label>Period end</Label><TextField fullWidth size="small" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} InputLabelProps={{ shrink: true }} /></Box>
          <Box sx={{ width: "6.5rem" }}><Label>Scale (max)</Label><TextField fullWidth size="small" type="number" value={scaleMax} onChange={(e) => setScaleMax(e.target.value)} /></Box>
        </Stack>

        <Label>Competencies (what's rated · weight)</Label>
        <Stack spacing={1} sx={{ mb: "0.75rem" }}>
          {competencies.map((c, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Autocomplete
                freeSolo
                fullWidth
                size="small"
                value={c.name}
                // don't offer one that is already on this cycle
                options={known
                  .filter((k) => !competencies.some((row, idx) => idx !== i && row.name.trim() === k.name))
                  .map((k) => k.name)}
                onInputChange={(e, v) => setComp(i, "name", v)}
                onChange={(e, v) => {
                  setComp(i, "name", v || "");
                  // carry the weight it was last given, so picking a known
                  // competency reproduces how it was weighted before
                  const match = known.find((k) => k.name === v);
                  if (match) setComp(i, "weight", match.weight);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="e.g. Delivery" />
                )}
              />
              <TextField size="small" type="number" value={c.weight} onChange={(e) => setComp(i, "weight", e.target.value)} sx={{ width: "5rem" }} />
              {competencies.length > 1 && <IconButton size="small" onClick={() => setCompetencies((p) => p.filter((_, idx) => idx !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>}
            </Stack>
          ))}
        </Stack>
        <BtnSecondary onClick={() => setCompetencies((p) => [...p, { name: "", weight: 1 }])} sx={{ borderRadius: "8px", mb: "1.5rem" }}>
          <AddIcon fontSize="small" sx={{ mr: "0.25rem" }} /> Add competency
        </BtnSecondary>

        {/* Questions used to be edited here. They now have their own screen:
            they are configuration rather than part of setting a cycle up, and a
            set outlives the form — it is read long after the cycle is running,
            when this dialog refuses to open at all. */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={submit} disabled={busy} sx={{ borderRadius: "8px" }}>
            {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : cycle ? "Save changes" : "Create cycle"}
          </BtnPrimary>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default CycleFormDialog;
